import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Ticket, Minus, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DoorSalePage() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}/door-config`)
      .then(r => setData(r.data))
      .catch(() => setNotFound(true));
  }, [eventId]);

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Event not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const ev = data.event || {};
  const tier = data.door_tier;
  const evName = ev.name || ev.title;

  const buy = async () => {
    if (!name.trim() || !email.includes('@')) return toast.error('Your name and a valid email are required');
    setPaying(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/purchase`, {
        items: [{ ticket_type_id: tier.ticket_type_id, quantity: qty }],
        name, email, embed_source: 'door',
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else if (r.data.free && r.data.wallet_url) {
        window.location.href = r.data.wallet_url;
      } else if (r.data.order_id) {
        window.location.href = `/tickets/confirmation?order_id=${r.data.order_id}&door=1`;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-10 px-4">
      <div className="max-w-sm mx-auto space-y-4" data-testid="door-sale-page">
        {ev.image_url && <img src={ev.image_url} alt="" className="w-full h-36 object-cover rounded-xl" />}
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-widest text-emerald-400 font-bold">🎟 Door ticket</p>
          <h1 className="text-xl font-bold mt-1">{evName}</h1>
          <p className="text-sm text-muted-foreground">{ev.event_date}{ev.venue_name ? ` · ${ev.venue_name}` : ''}</p>
        </div>
        {!tier || tier.available <= 0 ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="door-sale-unavailable">
            Door tickets aren't available right now — ask the person at the door.
          </CardContent></Card>
        ) : (
          <Card className="border-emerald-500/40"><CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm flex items-center gap-1.5"><Ticket className="h-4 w-4 text-emerald-400" /> {tier.name}</p>
                <p className="text-xs text-muted-foreground">{tier.available} left at the door</p>
              </div>
              <p className="text-xl font-extrabold text-emerald-400">${Number(tier.price).toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-center gap-4">
              <Button type="button" variant="outline" size="icon" className="h-9 w-9" disabled={qty <= 1} onClick={() => setQty(q => q - 1)} data-testid="door-qty-minus"><Minus className="h-4 w-4" /></Button>
              <span className="text-2xl font-bold w-10 text-center" data-testid="door-qty-value">{qty}</span>
              <Button type="button" variant="outline" size="icon" className="h-9 w-9" disabled={qty >= Math.min(10, tier.available)} onClick={() => setQty(q => q + 1)} data-testid="door-qty-plus"><Plus className="h-4 w-4" /></Button>
            </div>
            <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} data-testid="door-buyer-name" />
            <Input type="email" placeholder="Email (your ticket goes here)" value={email} onChange={e => setEmail(e.target.value)} data-testid="door-buyer-email" />
            <Button className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white" disabled={paying} onClick={buy} data-testid="door-pay-btn">
              {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : `Pay $${(tier.price * qty).toFixed(2)} — get my ticket`}
            </Button>
            <p className="text-[10px] text-center text-muted-foreground">Your QR ticket appears on screen right after payment (and lands in your email).</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
